/* ==========================================================================
   Provider Page Header
   Shared header for provider pages with user info, profile link, and logout
   ========================================================================== */

import { Link, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { usePortalSlug } from '@/context/PortalContext'
import { usePortalThemeValues } from '@/hooks/usePortalTheme'
import { WhatsNewLink } from '@/components/changelog/WhatsNewBadge'

export function ProviderPageHeader() {
  const { user, logout } = useAuth()
  const slug = usePortalSlug()
  const navigate = useNavigate()
  const { logoUrl, pharmacyName } = usePortalThemeValues()

  async function handleLogout() {
    await logout()
    navigate(`/rx/${slug}/login`)
  }

  return (
    <div className="mb-6">
      {logoUrl && (
        <div className="mb-4 flex justify-center">
          <img
            src={logoUrl}
            alt={pharmacyName}
            className="h-18 max-w-full md:max-w-[360px] w-auto object-contain"
          />
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <span className="font-serif text-lg text-[var(--pharmacy-primary)]">
            {pharmacyName}
          </span>
          <h1 className="font-serif text-2xl sm:text-3xl text-[var(--color-charcoal)]">
            Prescriptions
          </h1>
          {user && (
            <p className="mt-1 text-sm text-[var(--color-gray-600)]">
              Prescribing as {user.name || user.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <WhatsNewLink />
          <Link to={`/rx/${slug}/profile`}>
            <Button variant="ghost" size="sm">
              Edit Profile
            </Button>
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="p-2 text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] transition-colors rounded-md hover:bg-[var(--color-gray-100)]"
            aria-label="Log out"
            title="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
